import React, { useCallback, useEffect, useRef } from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { Button } from 'framework/ui/mui-builder';
import { Typography } from '@mui/material';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';

export interface PendingOperation {
  values: FormFieldsData;
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

interface ConfirmationUpdateMentorModalProps {
  isOpen: boolean;
  pendingOperation: PendingOperation | null;
  onConfirm: (values: FormFieldsData) => Promise<void | object>;
  onClose: () => void;
  title?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const ConfirmationUpdateMentorModal: React.FC<ConfirmationUpdateMentorModalProps> = ({
  isOpen,
  pendingOperation,
  onConfirm,
  onClose,
  title = 'Confirm Mentor Change',
  message = 'Are you sure you want to change the mentor? This will replace the current mentor assignment.',
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
}) => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pendingOperation || !isMountedRef.current) {
      onClose();
      return;
    }

    const { values, resolve, reject } = pendingOperation;

    onClose();

    try {
      const result = await onConfirm(values);
      if (isMountedRef.current) {
        resolve(result);
      }
    } catch (error) {
      if (isMountedRef.current) {
        reject(error);
      }
    }
  }, [pendingOperation, onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    if (pendingOperation) {
      pendingOperation.reject(new Error('Mentor change cancelled by user'));
    }
    onClose();
  }, [pendingOperation, onClose]);

  const handleModalToggle = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onToggle={handleModalToggle}
      hasCancelButton={false}
      buttons={
        <>
          <Button onClick={handleCancel} variant="outlined">
            {cancelButtonText}
          </Button>
          <Button onClick={handleConfirm} color="primary">
            {confirmButtonText}
          </Button>
        </>
      }
    >
      <Typography>{message}</Typography>
    </Modal>
  );
};

export default ConfirmationUpdateMentorModal;
